import { useEffect, useState } from "react";
import { GraduationCap, ArrowRight, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

export async function downloadPromotionReport(runId) {
  try {
    const res = await api.get(`/students/promotion-report.pdf?run_id=${runId || ""}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = "promotion-report.pdf"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("No report available yet"); }
}

/** "New school year" — move every grade up one level, graduate the top grade. */
export function PromotionDialog({ open, onOpenChange }) {
  const [plan, setPlan] = useState([]);
  const [classes, setClasses] = useState([]);
  const [runs, setRuns] = useState([]);
  const [map, setMap] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, r, c] = await Promise.all([api.get("/students/promotion-plan"), api.get("/students/promotion-runs"), api.get("/classes")]);
        setPlan(p.data); setRuns(r.data); setClasses(c.data);
        setMap(Object.fromEntries(p.data.map((x) => [x.class_id, x.suggested_to])));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load plan"); onOpenChange(false); }
    })();
  }, [open, onOpenChange]);

  const run = async () => {
    const mappings = plan.filter((p) => map[p.class_id] && map[p.class_id] !== p.class_id).map((p) => ({ from_class_id: p.class_id, to_class_id: map[p.class_id] }));
    if (mappings.length === 0) { toast.error("Nothing to promote — pick destinations first."); return; }
    if (!window.confirm("Promote all students to their new grades? Graduating students will be archived. This cannot be undone in bulk.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/students/promote", { mappings });
      toast.success(`${data.promoted} student(s) promoted${data.graduated ? `, ${data.graduated} graduated & archived` : ""}`);
      if (data.run_id) downloadPromotionReport(data.run_id);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Promotion failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="promotion-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><GraduationCap className="h-5 w-5 text-primary" /> New school year</DialogTitle>
          <DialogDescription>Each grade moves to the suggested next grade — adjust if needed. The top grade graduates (students are archived, history kept).</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {plan.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No grades with students yet.</div>}
          {plan.map((p) => (
            <div key={p.class_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`promo-row-${p.class_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="text-[11px] text-slate-500">{p.count} student{p.count === 1 ? "" : "s"}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
              <select value={map[p.class_id] || ""} onChange={(e) => setMap({ ...map, [p.class_id]: e.target.value })} data-testid={`promo-to-${p.class_id}`}
                className="w-44 h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:border-primary outline-none">
                <option value="">Stay (no change)</option>
                {classes.filter((c) => c.id !== p.class_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="graduate">Graduate (archive)</option>
              </select>
            </div>
          ))}
        </div>
        {runs.length > 0 && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid="promotion-history">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">Past promotions</div>
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-1 text-xs text-slate-600">
                <span>{new Date(r.ran_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })} · {r.promoted} promoted, {r.graduated} graduated</span>
                <button onClick={() => downloadPromotionReport(r.id)} data-testid={`promo-report-${r.id}`} className="inline-flex items-center gap-1 font-medium text-primary hover:underline"><FileDown className="h-3 w-3" /> Report</button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <button onClick={run} disabled={busy} data-testid="promotion-run-btn" className="h-10 rounded-lg bg-primary px-6 text-sm font-medium text-white inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-50 transition">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />} Promote all students
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
